/**
 * BlindajeCanal — disuasión + registro ante intentos de hacerse con el material.
 *
 * Ann 2026-07-16 (caso Maria Pia): cuando alguien intenta descargar/copiar/guardar
 * un vídeo del Canal (clic derecho, Ctrl/Cmd+S, Ctrl+U), se le muestra un AVISO
 * LEGAL muy disuasorio CON SU NOMBRE (efecto "te tengo fichada") y el intento se
 * REGISTRA (track → canal_comportamiento_log) para el listado de vigilancia del
 * botón "Canal" en Estadísticas.
 *
 * Se monta UNA vez en la raíz del canal del cliente → cubre TODOS los reproductores
 * sin tocar cada uno. Bilingüe ES/CA (regla: catalán 100%, cero fugas). Sin emojis
 * en UI: el aviso usa icono SVG (ShieldAlert), no caracteres emoji.
 *
 * Honestidad interna (NO va en el aviso): las "trazas" reales hoy = registro de
 * identidad/IP/fecha del acceso + marca de agua visible del reproductor. La huella
 * forense invisible es futura. El aviso es disuasión legítima (el contenido SÍ está
 * protegido y el intento SÍ se registra), no una capacidad inventada.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, X } from 'lucide-react';
import { track } from '../../utils/track';

const TEXTOS = {
  es: {
    titulo: 'ADVERTENCIA LEGAL · INTENTO DE DESCARGA DETECTADO',
    p1: 'Estás intentando descargar, copiar o guardar contenido protegido. Este acto es ILEGAL.',
    p2: (nombre) => `Todo el material del Canal Método Pilar Domínguez está protegido con trazas de seguridad únicas que identifican tu cuenta${nombre ? ` — ${nombre} — ` : ' '}en cada reproducción.`,
    p3: 'Este intento ACABA DE QUEDAR REGISTRADO con tu identidad, fecha, hora e IP, y puede aportarse como PRUEBA en un procedimiento legal.',
    p4: 'La descarga, copia, grabación o distribución no autorizada constituye una infracción grave de los derechos de propiedad intelectual (Ley de Propiedad Intelectual — RDL 1/1996 — y normativa de la UE), perseguible por vía civil y penal, con reclamación de daños y perjuicios.',
    p5: 'Método Pilar Domínguez perseguirá cualquier uso indebido hasta las últimas consecuencias. El abuso puede conllevar el corte del servicio según la normativa del Canal.',
    copyright: '© Método Pilar Domínguez — Todos los derechos reservados.',
    boton: 'He entendido',
    limiteFallback: 'Hoy ya has hecho esta clase. El canal no permite repetir la misma clase dos veces el mismo día.',
    // Aviso SUAVE de los primeros intentos (Ann 2026-07-17).
    suave: 'Acción no permitida. La descarga de vídeos no está permitida.',
  },
  ca: {
    titulo: 'ADVERTIMENT LEGAL · INTENT DE DESCÀRREGA DETECTAT',
    p1: 'Estàs intentant descarregar, copiar o desar contingut protegit. Aquest acte és IL·LEGAL.',
    p2: (nombre) => `Tot el material del Canal Mètode Pilar Domínguez està protegit amb traces de seguretat úniques que identifiquen el teu compte${nombre ? ` — ${nombre} — ` : ' '}a cada reproducció.`,
    p3: 'Aquest intent ACABA DE QUEDAR REGISTRAT amb la teva identitat, data, hora i IP, i pot aportar-se com a PROVA en un procediment legal.',
    p4: 'La descàrrega, còpia, gravació o distribució no autoritzada constitueix una infracció greu dels drets de propietat intel·lectual (Llei de Propietat Intel·lectual — RDL 1/1996 — i normativa de la UE), perseguible per via civil i penal, amb reclamació de danys i perjudicis.',
    p5: "Mètode Pilar Domínguez perseguirà qualsevol ús indegut fins a les darreres conseqüències. L'abús pot comportar el tall del servei segons la normativa del Canal.",
    copyright: '© Mètode Pilar Domínguez — Tots els drets reservats.',
    boton: 'Ho he entès',
    limiteFallback: "Avui ja has fet aquesta classe. El canal no permet repetir la mateixa classe dues vegades el mateix dia.",
    // Avís SUAU dels primers intents (Ann 2026-07-17).
    suave: 'Acció no permesa. La descàrrega de vídeos no està permesa.',
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// AVISO PROGRESIVO · propuesta de Ann (17-jul-2026), textual:
//   "Primer clic derecho, alerta simple: 'acción no permitida, la descarga de
//    vídeos no está permitida'. A la 5ª vez salta la alerta gorda."
//
// EL PORQUÉ (caso real que lo motiva): Elsa Bertran, clienta Club que hace su
// clase a las 5 de la mañana 3 días seguidos, hizo 2 clics derecho el 17-jul a
// las 05:43. Le saltó el aviso legal ENTERO con su nombre. Ann: "debió meterse un
// buen susto". En meses solo ha habido 3 clics derecho en TODO el canal.
//   👉 No se asusta a quien tiene curiosidad. Se avisa en serio a quien INSISTE.
//
// ⚠️ EL REGISTRO NO CAMBIA: se sigue registrando DESDE EL PRIMER intento
//    (track → canal_comportamiento_log). Lo progresivo es lo que VE la clienta,
//    no lo que Ann sabe. La prueba se guarda igual.
//
// El contador vive en localStorage: es solo para la ESCALADA visual. Si alguien
// lo borra, da igual — la prueba está en el servidor, no aquí.
// ══════════════════════════════════════════════════════════════════════════════
const AVISO_GORDO_A_PARTIR_DE = 5;   // Ann: "a la 5ª vez salta la alerta gorda"
const CLAVE_INTENTOS = 'mpd_blindaje_intentos';

const leerIntentos = () => {
  try { return parseInt(localStorage.getItem(CLAVE_INTENTOS) || '0', 10) || 0; }
  catch (e) { return 0; }
};
const sumarIntento = () => {
  const n = leerIntentos() + 1;
  try { localStorage.setItem(CLAVE_INTENTOS, String(n)); } catch (e) { /* noop */ }
  return n;
};

const BlindajeCanal = ({ user, lang }) => {
  const [abierto, setAbierto] = useState(false);
  const [suaveMsg, setSuaveMsg] = useState('');
  const [zona, setZona] = useState(null);   // dónde está el vídeo (para plantar el aviso encima)
  const [limiteMsg, setLimiteMsg] = useState('');
  const idioma = (lang || (typeof document !== 'undefined' && document.documentElement.lang) || 'es').startsWith('ca') ? 'ca' : 'es';
  const t = TEXTOS[idioma];
  const nombre = (user && (user.nombre || user.name)) ? String(user.nombre || user.name).trim() : '';

  const disparar = useCallback((tipo, detalle, rect) => {
    // 1) REGISTRAR SIEMPRE, desde el primer intento. Esto NO es negociable:
    //    es la prueba y el listado de vigilancia de Ann.
    try { track(tipo, detalle); } catch (e) { /* noop */ }
    // 2) Y solo entonces decidir QUÉ VE ella (Ann: suave hasta la 5ª).
    const n = sumarIntento();
    if (n >= AVISO_GORDO_A_PARTIR_DE) setAbierto(true);
    else { setZona(rect || null); setSuaveMsg(t.suave); }
  }, [t.suave]);

  useEffect(() => {
    // Clic derecho sobre un vídeo (o su contenedor) → aviso + registro.
    const onContext = (e) => {
      const el = e.target;
      const esVideo = el && (el.tagName === 'VIDEO' || (el.closest && el.closest('video, [data-blindaje-canal]')));
      if (esVideo) {
        e.preventDefault();
        // Ann 17-jul: "pon este aviso SOBRE el vídeo" · "que se vea bien".
        // Guardamos dónde está el vídeo para plantar el aviso justo encima.
        let rect = null;
        try {
          const v = el.tagName === 'VIDEO' ? el : el.closest('video, [data-blindaje-canal]');
          const r = v && v.getBoundingClientRect ? v.getBoundingClientRect() : null;
          if (r && r.width > 40 && r.height > 40) rect = { top: r.top, left: r.left, w: r.width, h: r.height };
        } catch (_) { /* si no se puede, cae al centro de la pantalla */ }
        disparar('clic_derecho', 'canal', rect);
      }
    };
    // Ctrl/Cmd+S (guardar) y Ctrl+U (ver fuente) dentro del canal → aviso + registro.
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === 's' || k === 'u')) {
        e.preventDefault();
        disparar('intento_guardar', 'canal');
      }
    };
    // Límite anti-repetición (429): el reproductor emite este evento con el mensaje.
    const onLimite = (e) => { setLimiteMsg((e && e.detail) || t.limiteFallback); };
    document.addEventListener('contextmenu', onContext, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('canal:limite-repeticion', onLimite);
    return () => {
      document.removeEventListener('contextmenu', onContext, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('canal:limite-repeticion', onLimite);
    };
  }, [disparar, t.limiteFallback]);

  // El aviso suave se va solo a los 4 s (no obliga a hacer clic en nada).
  useEffect(() => {
    if (!suaveMsg) return undefined;
    const id = setTimeout(() => setSuaveMsg(''), 4000);
    return () => clearTimeout(id);
  }, [suaveMsg]);

  // ── AVISO SUAVE (intentos 1 a 4) · Ann 17-jul-2026 ──────────────────────────
  // "Acción no permitida. La descarga de vídeos no está permitida."
  // Ni tarjeta blanca ni modal: una banda oscura SOBRE el vídeo, se va sola.
  // Ann: "seremos tema de conversación en vestuarios y bares — 'le di al botón
  // derecho y me saltó una alerta' — y así las que están para robar se irán por
  // donde vinieron". Tiene que verse. NO tiene que asustar.
  // Sin emojis: icono SVG (norma de marca).
  // ── AVISO SUAVE (intentos 1 a 4) · Ann 17-jul-2026 ──────────────────────────
  // "Acción no permitida. La descarga de vídeos no está permitida."
  // Ann: "pon este aviso SOBRE el vídeo" · "que se vea bien".
  // Va ENCIMA del vídeo que ha tocado, tapándolo con un velo oscuro y letras
  // grandes. Se va solo a los 4 s (no obliga a cerrar nada) y no bloquea el resto
  // de la página. Si por lo que sea no se sabe dónde está el vídeo, sale centrado
  // en la pantalla (nunca se queda sin verse).
  // Ann: "seremos tema de conversación en vestuarios y bares — 'le di al botón
  // derecho y me saltó una alerta' — y así las que están para robar se irán por
  // donde vinieron". Tiene que VERSE. NO tiene que asustar.
  // Sin emojis: icono SVG (norma de marca).
  const avisoSuave = suaveMsg ? (
    <div
      role="status"
      aria-live="polite"
      onClick={() => setSuaveMsg('')}
      style={{
        position: 'fixed',
        top: zona ? zona.top : '50%',
        left: zona ? zona.left : '50%',
        width: zona ? zona.w : 'min(90vw,520px)',
        height: zona ? zona.h : 'auto',
        transform: zona ? 'none' : 'translate(-50%,-50%)',
        zIndex: 9998,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 14,
        textAlign: 'center', padding: 'clamp(16px,4vw,32px)',
        borderRadius: 12, cursor: 'pointer',
        background: 'linear-gradient(180deg,rgba(8,8,10,.80) 0%,rgba(8,8,10,.94) 100%)',
        fontFamily: "'Montserrat','Helvetica Neue',Arial,sans-serif",
        animation: 'mpdAvisoEntra .18s ease-out',
      }}
    >
      <ShieldAlert size={44} color="#ff4d3d" style={{ flexShrink: 0 }} />
      <span style={{
        // Ann 17-jul: "y la letra en ROJO". Mismo rojo que el aviso del límite
        // diario (#ff4d3d) → el canal habla con una sola voz cuando dice NO.
        color: '#ff4d3d', fontSize: 'clamp(16px,2.4vw,22px)', fontWeight: 800,
        lineHeight: 1.4, maxWidth: '92%', textShadow: '0 2px 12px rgba(0,0,0,.85)',
      }}>
        {suaveMsg}
      </span>
      <style>{`@keyframes mpdAvisoEntra{from{opacity:0}to{opacity:1}}
        @media (prefers-reduced-motion: reduce){[role="status"]{animation:none !important}}`}</style>
    </div>
  ) : null;

  // Aviso del LÍMITE diario (mensaje textual de Ann, vía 429).
  // Diseño pedido por Ann (2026-07-16): flotando SOBRE el vídeo, letras ROJAS y
  // un degradado oscuro que TAPA la imagen (no una tarjeta blanca).
  if (limiteMsg) {
    return (
      <div role="alertdialog" aria-modal="true" onClick={() => setLimiteMsg('')}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: 24,
          background: 'linear-gradient(180deg,rgba(8,8,10,.72) 0%,rgba(8,8,10,.93) 55%,rgba(8,8,10,.98) 100%)',
          fontFamily: "'Montserrat','Helvetica Neue',Arial,sans-serif",
        }}>
        <ShieldAlert size={38} color="#ff4d3d" />
        <p onClick={(e) => e.stopPropagation()}
          style={{
            margin: '14px 0 20px', maxWidth: 380, fontSize: 16, fontWeight: 700, lineHeight: 1.55,
            color: '#ff4d3d', textShadow: '0 2px 10px rgba(0,0,0,.6)',
          }}>{limiteMsg}</p>
        <button onClick={(e) => { e.stopPropagation(); setLimiteMsg(''); }}
          style={{
            background: 'transparent', color: '#ff4d3d', border: '1.5px solid #ff4d3d',
            borderRadius: 10, padding: '11px 26px', fontWeight: 700, fontSize: 14,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
          {t.boton}
        </button>
      </div>
    );
  }

  // ⚠️ El aviso suave se devuelve AQUÍ: si no, este `return null` se lo comería
  //    y no se vería nunca (los intentos 1-4 no abren el aviso gordo).
  //    Todos los hooks están ARRIBA de este return — regla de React que ya me
  //    costó dejar a 442 clientas sin firmar el 16-jul (#1303).
  if (!abierto) return avisoSuave;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      onClick={() => setAbierto(false)}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(10,10,12,.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: "'Montserrat','Helvetica Neue',Arial,sans-serif" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 16, border: '2px solid #8a1f14', boxShadow: '0 20px 60px rgba(0,0,0,.5)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', background: 'linear-gradient(90deg,#7a1a10,#a5301f)', borderRadius: '13px 13px 0 0' }}>
          <ShieldAlert size={26} color="#fff" style={{ flexShrink: 0 }} />
          <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '.3px', lineHeight: 1.3 }}>{t.titulo}</div>
          <button onClick={() => setAbierto(false)} aria-label="Cerrar" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 2 }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: '18px 22px', color: '#1c1c1e', fontSize: 14, lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#8a1f14' }}>{t.p1}</p>
          <p style={{ margin: '0 0 12px' }}>{t.p2(nombre)}</p>
          <p style={{ margin: '0 0 12px', fontWeight: 700 }}>{t.p3}</p>
          <p style={{ margin: '0 0 12px', color: '#3a3a3c' }}>{t.p4}</p>
          <p style={{ margin: '0 0 14px', fontWeight: 600 }}>{t.p5}</p>
          <p style={{ margin: 0, fontSize: 12, color: '#6b6b70' }}>{t.copyright}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
            <button onClick={() => setAbierto(false)} style={{ background: '#8a1f14', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 26px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              {t.boton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlindajeCanal;
